/* מבט שנתי: היתרה בסוף כל חודש, הכנסות מול הוצאות, וטבלה של כל התקופות */
import { useState } from 'react';
import { S } from '../../domain/state.js';
import { fMoney, fShort } from '../../lib/format.js';
import { periodTiny, periodShort } from '../../domain/periods.js';
import { yearData } from '../../domain/review.js';
import { Empty } from '../../components/ui.jsx';
import { useApp } from '../../store/ui.js';
import { openReview } from './Review.jsx';

export function YearPage() {
  useApp();
  const rows = yearData();
  if (!rows.length) return <div className="period"><Empty emo="📊">עוד אין נתונים. אחרי סריקת הדוח הראשון יופיע כאן מבט על כל החודשים.</Empty></div>;
  const closed = rows.filter((r) => r.closed && r.hasData);
  const tInc = closed.reduce((s, r) => s + r.inc, 0), tExp = closed.reduce((s, r) => s + r.exp, 0);
  const avgNet = closed.length ? (tInc - tExp) / closed.length : 0;
  const best = closed.slice().sort((a, b) => b.net - a.net)[0], worst = closed.slice().sort((a, b) => a.net - b.net)[0];
  const hasBal = !!S.set.balDate && rows.some((r) => r.close != null);
  return (
    <div className="stack">
      <div className="kpis">
        <div className="kpi-tile"><div className="kpi-k">הכנסות ({closed.length} חודשים)</div><div className="kpi-v green">{fShort(tInc)}</div></div>
        <div className="kpi-tile"><div className="kpi-k">הוצאות</div><div className="kpi-v red">{fShort(tExp)}</div></div>
        <div className="kpi-tile"><div className="kpi-k">נשאר בממוצע בחודש</div><div className="kpi-v" style={{ color: avgNet < 0 ? 'var(--red)' : 'var(--green)' }}>{fMoney(avgNet)}</div></div>
        {best && closed.length > 1 && <div className="kpi-tile"><div className="kpi-k">החודש הכי טוב / הכי חלש</div>
          <div className="kpi-v" style={{ fontSize: '1.05rem' }}><span className="green">{periodTiny(best.k)}</span> / <span className="red">{periodTiny(worst.k)}</span></div></div>}
      </div>
      <div className="cols even">
        <div className="col">
          {hasBal && <div className="panel"><div className="sec">יתרה בעו"ש בסוף כל חודש</div><BalanceChart rows={rows.filter((r) => r.close != null)} /></div>}
          <div className="panel"><div className="sec">הכנסות מול הוצאות</div><BarsChart rows={rows} /></div>
        </div>
        <div className="col">
          <div className="panel">
            <div className="sec">כל החודשים <span className="sec-sub">לחיצה פותחת סיכום</span></div>
            <div className="ytable">
              <span className="th">תקופה</span><span className="th num">הכנסות</span><span className="th num">הוצאות</span><span className="th num">נשאר</span>{hasBal && <span className="th num">יתרה בסוף</span>}
              {rows.slice().reverse().map((r) => (
                <div key={r.k} className={'yrow' + (hasBal ? ' bal' : '')} onClick={() => openReview(r.k)} role="button" tabIndex={0}>
                  <span>{periodShort(r.k)}{!r.closed && <span className="muted"> (עכשיו)</span>}</span>
                  <span className="num green">{fShort(r.inc)}</span>
                  <span className="num red">{fShort(r.exp)}</span>
                  <span className="num" style={{ color: r.net < 0 ? 'var(--red)' : 'var(--green)' }}>{fShort(r.net)}</span>
                  {hasBal && <span className="num" style={{ color: r.close < 0 ? 'var(--red)' : undefined }}>{r.close != null ? fShort(r.close) : '-'}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** קו היתרה בסוף כל תקופה. הקו ב-SVG נמתח לרוחב, והתוויות ב-HTML כדי שיישארו קריאות בכל מסך */
function BalanceChart({ rows }) {
  const [hi, setHi] = useState(-1);
  if (rows.length < 2) return <div className="note">הגרף יופיע אחרי שתי תקופות סגורות.</div>;
  const vals = rows.map((r) => r.close);
  const max = Math.max(0, ...vals), min = Math.min(0, ...vals), span = max - min || 1;
  const y = (v) => ((max - v) / span) * 100;
  const x = (i) => (rows.length === 1 ? 50 : 4 + (i / (rows.length - 1)) * 92);
  const pts = vals.map((v, i) => x(i) + ',' + y(v)).join(' ');
  const sel = hi >= 0 ? rows[hi] : rows[rows.length - 1];
  return (
    <div dir="ltr">
      <div className="ch-sel" dir="rtl"><span className="muted">{periodShort(sel.k)}:</span> <b style={{ color: sel.close < 0 ? 'var(--red)' : 'var(--green)' }}>{fMoney(sel.close)}</b></div>
      <div className="ch-area">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="ch-svg" aria-hidden="true">
          <line x1="0" x2="100" y1={y(0)} y2={y(0)} className="ch-zero" vectorEffect="non-scaling-stroke" />
          <polyline points={pts} className="ch-line" vectorEffect="non-scaling-stroke" />
        </svg>
        {vals.map((v, i) => (
          <button key={i} className={'ch-dot' + (v < 0 ? ' neg' : '') + (i === hi ? ' on' : '')} style={{ left: x(i) + '%', top: y(v) + '%' }}
            onMouseEnter={() => setHi(i)} onFocus={() => setHi(i)} onClick={() => setHi(i)} aria-label={periodShort(rows[i].k) + ' ' + fMoney(v)} />
        ))}
        <span className="ch-axis" style={{ top: y(0) + '%' }}>0</span>
        {/* קצוות הסקאלה, כשהם לא צמודים לקו האפס */}
        {min < 0 && y(min) - y(0) > 12 && <span className="ch-axis" style={{ top: y(min) + '%' }}>{fShort(min)}</span>}
        {max > 0 && y(0) - y(max) > 12 && <span className="ch-axis" style={{ top: y(max) + '%' }}>{fShort(max)}</span>}
      </div>
      <div className="ch-labels">{rows.map((r, i) => <span key={r.k} style={{ left: x(i) + '%' }}>{periodTiny(r.k)}</span>)}</div>
    </div>
  );
}

function BarsChart({ rows }) {
  const max = Math.max(1, ...rows.map((r) => Math.max(r.inc, r.exp)));
  return (
    <div dir="ltr">
      <div className="bars">
        {rows.map((r) => (
          <div key={r.k} className="bar-col" title={periodShort(r.k) + ': הכנסות ' + fMoney(r.inc) + ', הוצאות ' + fMoney(r.exp)} onClick={() => openReview(r.k)}>
            <div className="bar-pair">
              <i className="b-inc" style={{ height: (r.inc / max) * 100 + '%' }} />
              <i className="b-exp" style={{ height: (r.exp / max) * 100 + '%', opacity: r.closed ? 1 : 0.5 }} />
            </div>
            <span>{periodTiny(r.k)}</span>
          </div>
        ))}
      </div>
      <div className="ch-legend" dir="rtl"><span><i className="b-inc" />הכנסות</span><span><i className="b-exp" />הוצאות</span><span className="muted">החודש הנוכחי שקוף, כי הוא עוד לא נגמר</span></div>
    </div>
  );
}
